import { css } from 'aphrodite';
import moment from 'moment';
import * as React from 'react';
import { styleSheet } from './styles';

/**
 * What each parent's approval does with a date that has already passed. The rules are the servers'
 * own and they disagree, which is why this is stated once rather than per host:
 *
 * - `email` — ApproveAsync refuses a start date more than two days old.
 * - `post` — ApproveAsync refuses any past dueDate, with no grace period at all.
 * - `blog` — no guard; approval publishes a past-dated blog immediately rather than on its date.
 */
export type RegEdReviseDateKind = 'email' | 'post' | 'blog';

const REVISE_UNAPPROVABLE_EMAIL_DAYS = 2;

/**
 * Whether a revision can be submitted against this date, and what to tell the advisor. Exported
 * because the host needs the verdict to hold its submit button while the dialog shows the reason.
 */
export const getRegEdReviseDateState = (kind: RegEdReviseDateKind, date: Date | null | undefined) => {
	if (!date) {
		return { blocksApproval: false, isPast: false, message: null as string | null };
	}
	const isPast = moment(date).isBefore(moment());
	if (!isPast) {
		return { blocksApproval: false, isPast: false, message: null as string | null };
	}
	if (kind === 'email') {
		const blocksApproval = moment().diff(moment(date), 'days', true) > REVISE_UNAPPROVABLE_EMAIL_DAYS;
		return {
			blocksApproval,
			isPast,
			message: blocksApproval
				? 'This send time is more than two days past, which compliance cannot approve. Choose a new time so the review can complete.'
				: 'This send time has passed. Once compliance approves it, the campaign is released and goes out in the next send window.',
		};
	}
	if (kind === 'post') {
		return {
			blocksApproval: true,
			isPast,
			message:
				'This posting date has passed, which compliance cannot approve. Choose a new date so the review can complete.',
		};
	}
	return {
		blocksApproval: false,
		isPast,
		message:
			'This publish date has passed. Once compliance approves it, the post is released and goes out in the next window.',
	};
};

const NOUNS: Record<RegEdReviseDateKind, { subject: string; slot: string }> = {
	blog: { subject: 'publish date', slot: 'date' },
	email: { subject: 'send time', slot: 'time' },
	post: { subject: 'posting date', slot: 'date' },
};

/** Reads as a caption on the schedule card above it, which is why it carries that card's inset. */
export const RegEdReviseDateNotice = ({
	carriedFrom,
	date,
	kind,
}: {
	/** Where a stale date was moved from, when the revision refreshed it rather than keeping it. */
	carriedFrom?: Date | null;
	date: Date | null | undefined;
	kind: RegEdReviseDateKind;
}) => {
	const { blocksApproval, message } = getRegEdReviseDateState(kind, date);
	if (message) {
		return <div className={css(styleSheet.notice, blocksApproval && styleSheet.blocking)}>{message}</div>;
	}
	if (carriedFrom) {
		const { subject, slot } = NOUNS[kind];
		return (
			<div className={css(styleSheet.notice)}>
				{`The original ${subject}, ${moment(carriedFrom).format('ddd, MMM D [at] h:mm A')}, ${
					moment(carriedFrom).isBefore(moment()) ? 'has passed' : "falls inside your firm's review notice period"
				}, so it's scheduled for the ${slot} above instead.`}
			</div>
		);
	}
	return null;
};
